<template>
  <div class="feed-page">
    <!-- Dashboard Button (fixed) -->
    <v-btn
      color="primary"
      variant="outlined"
      size="small"
      prepend-icon="mdi-view-dashboard"
      @click="goBack"
      class="dashboard-button"
    >
      Dashboard
    </v-btn>

    <!-- BAND 1: Beige — logo only -->
    <div class="band band-hero">
      <div class="band-content">
        <div class="page-header">
          <div class="brand-header">
            <router-link to="/">
              <PassingOnLogo class="brand-logo-svg" :name2-override="memorial?.nameOfPerson ? '-' + memorial.nameOfPerson : null" />
            </router-link>
          </div>
          <router-link to="/" class="mobile-logo-link">
            <img src="/logo-red.png" alt="PassingOn" class="mobile-logo" />
          </router-link>
        </div>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-6"></v-progress-linear>
        <v-alert v-if="error" type="error" variant="tonal" class="mb-6">{{ error }}</v-alert>
      </div>
    </div>

    <template v-if="!loading && memorial">
      <!-- BAND 2: Dark — photo + name + details -->
      <div class="band band-details">
        <div class="band-content">
          <div class="hero-layout">
            <!-- Profile Photo -->
            <div class="hero-photo-container">
              <img
                v-if="memorial.profilePhoto"
                :src="getMediaUrl(memorial.profilePhoto)"
                :alt="memorial.nameOfPerson"
                class="hero-photo"
              />
              <div v-else class="hero-photo-placeholder">
                <v-icon size="120" color="grey-lighten-1">mdi-account</v-icon>
              </div>
            </div>

            <div class="hero-info">
              <div v-if="memorial.birthDate || memorial.deathDate || (hasEvent && (memorial.memorialDate || memorial.address || memorial.details))" class="details-inner">
                <h1 v-if="memorial.birthDate && memorial.deathDate" class="life-heading">
                  {{ memorial.nameOfPerson || 'Memory' }}
                  <span>{{ formatLifeDate(memorial.birthDate) }} - {{ formatLifeDate(memorial.deathDate) }}</span>
                </h1>

                <div v-if="memorial.birthDate && !memorial.deathDate" class="hero-detail-item">
                  <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-cake-variant-outline</v-icon>
                  <span>Born {{ formatLifeDate(memorial.birthDate) }}</span>
                </div>

                <div v-if="memorial.deathDate && !memorial.birthDate" class="hero-detail-item">
                  <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-flower-outline</v-icon>
                  <span>Died {{ formatLifeDate(memorial.deathDate) }}</span>
                </div>

                <!-- Date + address on the left, large map icon on the right spanning both rows -->
                <div v-if="hasEvent && (memorial.memorialDate || memorial.address)" class="details-top">
                  <div class="details-date-address">
                    <div v-if="memorial.memorialDate" class="hero-detail-item">
                      <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-calendar</v-icon>
                      <span>Event: {{ formatMemorialDate(memorial.memorialDate) }}<template v-if="memorial.memorialHour != null">&nbsp;&nbsp;{{ String(memorial.memorialHour).padStart(2, '0') }}:{{ String(memorial.memorialMinute ?? 0).padStart(2, '0') }}</template></span>
                    </div>
                    <div v-if="memorial.address" class="hero-detail-item">
                      <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-map-marker</v-icon>
                      <span>{{ memorial.address }}</span>
                    </div>
                  </div>
                  <img
                    v-if="memorial.address"
                    src="/images/map-icon.png"
                    alt=""
                    class="map-big-icon"
                    role="button"
                    tabindex="0"
                    @click="toggleMap"
                    @keydown.enter.prevent="toggleMap"
                    @keydown.space.prevent="toggleMap"
                  />
                </div>

                <!-- Details spans the full width (date + icon columns) -->
                <div v-if="hasEvent && memorial.details" class="hero-detail-item hero-details-text">
                  <span>{{ memorial.details }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Google Maps embed -->
          <div v-if="memorial.address && memorial.address.trim() && mapExpanded" class="hero-map mt-4">
            <iframe
              :src="mapSrc"
              width="100%"
              height="250"
              style="border:0; border-radius: 8px;"
              allowfullscreen
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>

      <!-- Red stripe separator -->
      <div class="band-red-stripe"></div>

      <div v-if="canManageContributions && pendingContributionsCount > 0" class="band pending-contributions-band">
        <div class="band-content pending-contributions-wrap">
          <v-alert
            type="warning"
            variant="tonal"
            border="start"
            class="pending-contributions-alert"
          >
            <div class="pending-contributions-content">
              <span>
                <strong>{{ pendingContributionsCount }}</strong>
                {{ pendingContributionsCount === 1 ? 'contribution needs' : 'contributions need' }} to be approved or blocked.
              </span>
              <v-btn
                color="warning"
                variant="outlined"
                size="small"
                append-icon="mdi-arrow-down"
                @click="scrollToContributions"
              >
                Review {{ pendingContributionsCount === 1 ? 'contribution' : 'contributions' }}
              </v-btn>
            </div>
          </v-alert>
        </div>
      </div>

      <!-- BAND 3: White — host photos + stories -->
      <div v-if="ownerPhotos.length > 0 || ownerAudios.length > 0 || ownerStories.length > 0" class="band band-host">
        <div class="band-content">
          <!-- Owner's Photos -->
          <div v-if="ownerPhotos.length > 0" class="host-section mb-8">
            <div class="contributor-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-account-circle</v-icon>
              <span class="contributor-name">From {{ ownerFirstName }}</span>
            </div>
            <div class="photos-list">
              <div v-for="photo in ownerPhotos" :key="photo.uuid" class="photo-row">
                <div class="photo-left">
                  <img :src="getMediaUrl(photo)" :alt="photo.title || 'Memory photo'" class="photo-image" />
                </div>
                <div v-if="photo.title" class="photo-right">
                  <p class="photo-caption">{{ photo.title }}</p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="ownerAudios.length > 0" class="host-section mb-8">
            <div class="contributor-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-account-voice</v-icon>
              <span class="contributor-name">Voice memories from {{ ownerFirstName }}</span>
            </div>
            <AudioEntry v-for="audio in ownerAudios" :key="audio.uuid" :media="audio" :memorial-slug="memorial.shareSlug" class="mb-4" />
          </div>

          <!-- Owner's Stories -->
          <div v-if="ownerStories.length > 0" class="host-section">
            <div class="contributor-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-account-circle</v-icon>
              <span class="contributor-name">From {{ ownerFirstName }}</span>
            </div>
            <div v-for="story in ownerStories" :key="story.uuid" class="host-message mb-3">
              <p>{{ story.text }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- BAND 4: White — contributions -->
      <div class="band band-contributions">
        <div class="band-content">
          <div v-if="sortedContributions.length > 0" ref="contributionsSection" class="contributions-section">
            <div class="d-flex align-center justify-space-between mb-6">
              <h2 class="section-title mb-0">
                <v-icon class="mr-2" color="primary">mdi-heart-multiple</v-icon>
                Contributions from Friends &amp; Family
              </h2>
              <v-chip
                v-if="unseenContributionsCount > 0"
                color="info"
                size="small"
                @click="scrollToFirstUnseen"
                style="cursor: pointer;"
              >
                <v-icon start size="16">mdi-new-box</v-icon>
                {{ unseenContributionsCount }} new
              </v-chip>
            </div>

            <div
              v-for="contribution in sortedContributions"
              :key="contribution.id"
              :ref="el => setContributionRef(el, contribution.id)"
              class="contribution-section mb-8"
              :data-contribution-id="contribution.id"
              :class="{ 'unseen-contribution': !contribution.dateSeen, 'unapproved-contribution': !contribution.dateApproved }"
            >
              <div v-if="blockedContributionIds.has(contribution.id)" class="blocked-placeholder">
                <v-icon size="24" color="grey-darken-1" class="mr-2">mdi-block-helper</v-icon>
                <span>Contribution blocked.</span>
              </div>

              <template v-else>
                <v-chip v-if="!contribution.dateSeen && !contribution.justSeen" color="info" size="small" class="new-badge white-text-chip" variant="flat">
                  <v-icon start size="14" color="white">mdi-new-box</v-icon>
                  New
                </v-chip>
                <v-chip v-if="contribution.justSeen" color="info" size="small" class="new-badge fade-out white-text-chip" variant="flat">
                  <v-icon start size="14" color="white">mdi-check</v-icon>
                  Seen
                </v-chip>

                <div class="contributor-header mb-4">
                  <v-avatar color="primary" size="40" class="mr-3">
                    <span class="text-white font-weight-bold">{{ getInitials(contribution.contributorName) }}</span>
                  </v-avatar>
                  <div>
                    <div class="contributor-name">{{ contribution.contributorName }}</div>
                    <div class="contributor-date text-caption text--secondary">{{ formatDate(contribution.dateCreated) }}</div>
                  </div>
                  <v-spacer></v-spacer>
                  <div class="contribution-actions">
                    <v-btn
                      v-if="!contribution.dateApproved"
                      color="success"
                      size="small"
                      :loading="approvingContributionIds.has(contribution.id)"
                      :disabled="approvingContributionIds.has(contribution.id) || blockingContributionIds.has(contribution.id)"
                      @click="approveContribution(contribution)"
                    >Approve</v-btn>
                    <v-btn
                      color="warning"
                      size="small"
                      :loading="blockingContributionIds.has(contribution.id)"
                      :disabled="blockingContributionIds.has(contribution.id) || approvingContributionIds.has(contribution.id)"
                      @click="blockContribution(contribution)"
                    >Block</v-btn>
                    <span v-if="contribution.dateApproved" class="approval-status text-caption">Approved</span>
                  </div>
                </div>

                <div v-if="contribution.message" class="contribution-message mb-4">
                  <p class="text-body-1">{{ contribution.message }}</p>
                </div>

                <div v-if="mediaPhotos(contribution.media).length > 0" class="photos-list mb-4">
                  <div v-for="photo in mediaPhotos(contribution.media)" :key="photo.uuid" class="photo-row">
                    <div class="photo-left">
                      <img :src="getMediaUrl(photo)" :alt="photo.title || 'Contribution photo'" class="photo-image" />
                    </div>
                    <div v-if="photo.title" class="photo-right">
                      <p class="photo-caption">{{ photo.title }}</p>
                    </div>
                  </div>
                </div>

                <div v-if="mediaAudios(contribution.media).length" class="mb-4">
                  <AudioEntry v-for="audio in mediaAudios(contribution.media)" :key="audio.uuid" :media="audio" :memorial-slug="memorial.shareSlug" class="mb-4" />
                </div>

                <v-divider v-if="sortedContributions.indexOf(contribution) < sortedContributions.length - 1" class="mt-6"></v-divider>
              </template>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="ownerPhotos.length === 0 && ownerAudios.length === 0 && ownerStories.length === 0 && sortedContributions.length === 0" class="empty-state text-center py-12">
            <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-image-off</v-icon>
            <h3 class="text-h6 mb-2">No Content Yet</h3>
            <p class="text--secondary">Photos and stories will appear here once added.</p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import { formatLifeDate } from '../utils/helpers'
import AudioEntry from '../components/media/AudioEntry.vue'

const router = useRouter()
const store = useMemorialStore()

const loading = ref(true)
const error = ref('')
const memorial = ref(null)
const approvingContributionIds = ref(new Set())
const blockingContributionIds = ref(new Set())
const blockedContributionIds = ref(new Set())
const contributionsSection = ref(null)

// Computed properties for organized content
const ownerFirstName = computed(() => memorial.value?.user?.firstName || 'The Family')

const ownerPhotos = computed(() => {
  if (!memorial.value?.media) return []
  // Filter out the profile photo
  return memorial.value.media.filter(m => m.uuid !== memorial.value.profilePhoto?.uuid && (m.mediaType === 0 || m.mediaType == null))
})

const ownerAudios = computed(() => memorial.value?.media?.filter(media => media.mediaType === 4) || [])
const mediaPhotos = media => (media || []).filter(item => item.mediaType === 0 || item.mediaType == null)
const mediaAudios = media => (media || []).filter(item => item.mediaType === 4)

const ownerStories = computed(() => {
  return memorial.value?.stories || []
})

const sortedContributions = computed(() => {
  if (!memorial.value?.contributions) return []
  // Keep contributions needing approval at the top, then oldest first within
  // each approval group (matching the existing feed/PDF chronology).
  return [...memorial.value.contributions].sort((a, b) => {
    const approvalOrder = Number(Boolean(a.dateApproved)) - Number(Boolean(b.dateApproved))
    if (approvalOrder !== 0) return approvalOrder

    return new Date(a.dateCreated) - new Date(b.dateCreated)
  })
})

const canManageContributions = computed(() => {
  const userUuid = store.user?.uuid
  if (!userUuid || !memorial.value) return false

  return memorial.value.user?.uuid === userUuid ||
    memorial.value.admins?.some(admin => admin.uuid === userUuid)
})

const pendingContributionsCount = computed(() => {
  return sortedContributions.value.filter(contribution => {
    return !contribution.dateApproved && !contribution.dateBlocked
  }).length
})

const unseenContributionsCount = computed(() => {
  return sortedContributions.value.filter(c => !c.dateSeen && !c.justSeen).length
})

// Memorials created before events became optional have no flag and always had one
const hasEvent = computed(() => {
  return memorial.value?.hasEvent !== false
})

const loadFeedData = async () => {
  loading.value = true
  error.value = ''

  try {
    // Check if user is authenticated
    if (!store.apiToken || !store.memorialUuid) {
      error.value = 'No memorial found. Please create or select a memorial.'
      return
    }

    // Load memorial data
    const memorialResponse = await apiService.getMemorial(store.memorialUuid)
    if (memorialResponse.success && memorialResponse.memorial) {
      memorial.value = memorialResponse.memorial
      if (!memorial.value?.address) {
        mapExpanded.value = false
      }
    } else {
      error.value = memorialResponse.msg || 'Failed to load memorial'
    }
  } catch (err) {
    console.error('Feed load error:', err)
    error.value = 'Unable to load feed. Please try again.'
  } finally {
    loading.value = false
  }
}

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatMemorialDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const mapExpanded = ref(false)

const mapSrc = computed(() => {
  if (!memorial.value?.address || !memorial.value.address.trim()) return ''
  return `https://www.google.com/maps?q=${encodeURIComponent(memorial.value.address.trim())}&output=embed`
})

const getMediaUrl = (media) => {
  if (media.location) {
    return `${media.location}/${media.filename}.${media.extension}`
  }
  return `https://assets.forever-x.com/photos/${media.filename}.${media.extension}`
}

const getInitials = (name) => {
  if (!name) return '?'
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const toggleMap = () => {
  mapExpanded.value = !mapExpanded.value
}

const goBack = () => {
  router.push('/memory/dashboard')
}

const scrollToContributions = () => {
  contributionsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const approveContribution = async (contribution) => {
  if (!store.apiToken) return
  approvingContributionIds.value.add(contribution.id)
  try {
    const response = await apiService.approveContribution(contribution.id, true, store.apiToken)
    if (response.success) {
      contribution.dateApproved = new Date().getTime()
    } else {
      error.value = response.msg || 'Unable to approve contribution.'
    }
  } catch (err) {
    console.error('Approve contribution error:', err)
    error.value = 'Unable to approve contribution. Please try again.'
  } finally {
    approvingContributionIds.value.delete(contribution.id)
  }
}

const blockContribution = async (contribution) => {
  if (!store.apiToken) return
  blockingContributionIds.value.add(contribution.id)
  try {
    const response = await apiService.blockContribution(contribution.id, store.apiToken)
    if (response.success) {
      contribution.dateBlocked = response.contribution?.dateBlocked || new Date().getTime()
      blockedContributionIds.value.add(contribution.id)
    } else {
      error.value = response.msg || 'Unable to block contribution.'
    }
  } catch (err) {
    console.error('Block contribution error:', err)
    error.value = 'Unable to block contribution. Please try again.'
  } finally {
    blockingContributionIds.value.delete(contribution.id)
  }
}

// Contribution visibility tracking
const contributionRefs = new Map()
let observer = null

const setContributionRef = (el, contributionId) => {
  if (el) {
    const element = el.$el || el
    contributionRefs.set(contributionId, element)
  } else {
    contributionRefs.delete(contributionId)
  }
}

const markContributionSeen = async (contributionId) => {
  try {
    const contribution = memorial.value?.contributions?.find(c => c.id === contributionId)
    if (!contribution || contribution.dateSeen || contribution.justSeen) {
      return
    }

    const response = await apiService.markContributionSeen(contributionId, store.apiToken)
    if (response.success) {
      contribution.dateSeen = response.contribution.dateSeen
      contribution.justSeen = true
      console.log('Marked contribution as seen:', contributionId)

      setTimeout(() => {
        contribution.justSeen = false
      }, 3000)
    }
  } catch (err) {
    console.error('Error marking contribution as seen:', err)
  }
}

const scrollToFirstUnseen = () => {
  const firstUnseen = sortedContributions.value.find(c => !c.dateSeen && !c.justSeen)
  if (firstUnseen) {
    const element = contributionRefs.get(firstUnseen.id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }
}

const setupContributionObserver = () => {
  if (observer) {
    observer.disconnect()
  }

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const contributionId = parseInt(entry.target.dataset.contributionId)
          if (contributionId) {
            markContributionSeen(contributionId)
          }
        }
      })
    },
    {
      threshold: 0.5
    }
  )

  contributionRefs.forEach((element) => {
    if (element && element instanceof Element) {
      observer.observe(element)
    }
  })
}

onMounted(async () => {
  await loadFeedData()

  await nextTick()
  setupContributionObserver()
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
/* ── Page ── */
.feed-page {
  min-height: 100vh;
}

.dashboard-button {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 1001;
}

/* ── Shared band structure ── */
.band {
  width: 100%;
}

.band-content {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

/* ── Band 1: Hero (beige/background) ── */
.band-hero {
  background: var(--v-theme-background);
}

.band-hero .band-content {
  padding-bottom: 1rem;
}

.page-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  /* symmetric gutters reserve room for the profile menu pinned to the right, so
     a long memorial name truncates before it can run underneath the icon */
  padding-left: 64px;
  padding-right: 64px;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  max-width: 100%;
}

/* The router-link wrapper has to be shrinkable too, otherwise the name never
   truncates; flex also removes the inline baseline gap under the brand text
   that was pushing the profile icon out of alignment with the title. */
.brand-header > a {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

/* The brand is text, not an image — a fixed height just makes the glyphs
   overflow the box. */
.brand-logo-svg {
  height: auto;
  width: auto;
  max-width: 100%;
  min-width: 0;
}

.mobile-logo-link {
  display: none;
}

.mobile-logo {
  height: 36px;
  width: auto;
}

.hero-layout {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 2rem;
}

.hero-photo-container {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.hero-photo {
  width: 300px;
  height: 300px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.hero-photo-placeholder {
  width: 300px;
  height: 300px;
  background: var(--v-theme-gray100);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.hero-info {
  text-align: left;
  max-width: 520px;
  width: 100%;
  align-self: flex-start;
}

.remembering-title {
  font-size: 1.25rem;
  color: #bdbdbd;
  margin-bottom: 0.35rem;
  font-weight: 400;
}

.hero-name {
  font-size: 2.5rem;
  font-weight: 400;
  color: #ffffff;
  margin: 0;
}

/* ── Band 2: Details (dark) ── */
.band-details {
  background: #1a1a1a;
  color: #e0e0e0;
}

.details-inner {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.life-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.75rem;
  margin: 0 0 0.35rem;
  color: #ffffff;
  font-family: var(--v-theme-headingFont);
  font-size: 2rem;
  font-weight: 400;
  line-height: 1.2;
}

.life-heading span {
  color: #e0e0e0;
  font-size: 1.15rem;
  white-space: nowrap;
}

.band-details .hero-detail-item {
  display: flex;
  align-items: center;
  color: #e0e0e0;
  font-size: 1rem;
}

.details-top {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.details-date-address {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
  min-width: 0;
}

.band-details .hero-details-text {
  line-height: 1.5;
  color: #bdbdbd;
}

.map-big-icon {
  width: 64px;
  height: 64px;
  display: block;
  cursor: pointer;
  flex-shrink: 0;
  object-fit: contain;
  opacity: 0.8;
}

.hero-map {
  border-radius: 8px;
  overflow: hidden;
}

/* ── Red stripe separator ── */
.band-red-stripe {
  width: 100%;
  height: 30px;
  background: #D4401A;
  flex-shrink: 0;
}

.pending-contributions-band {
  background: #ffffff;
}

.pending-contributions-wrap {
  padding-bottom: 0;
}

.pending-contributions-alert {
  border-radius: 0;
}

.pending-contributions-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

/* ── Band 3: Host content (white) ── */
.band-host {
  background: #ffffff;
}

.band-host .contributor-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: #1a1a1a;
}

.host-message {
  background: rgba(0, 0, 0, 0.04);
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border-left: 4px solid #D4401A;
}

.host-message p {
  margin: 0;
  color: #1a1a1a;
  line-height: 1.6;
  white-space: pre-wrap;
}

.band-host .photo-caption {
  color: #555555;
}

/* ── Band 4: Contributions (white) ── */
.band-contributions {
  background: #ffffff;
}

/* ── Shared content styles ── */
.photos-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.photo-row {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.photo-left {
  flex-shrink: 0;
  width: 280px;
}

.photo-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

.photo-right {
  flex: 1;
  padding-top: 0.25rem;
}

.photo-caption {
  font-size: 1rem;
  color: var(--v-theme-gray600);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  display: flex;
  align-items: center;
}

/* Contribution cards */
.contribution-section {
  position: relative;
  padding: 16px;
  background: white;
  border-radius: 0;
  border: 1px solid transparent;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.unseen-contribution {
  border-color: rgb(var(--v-theme-info)) !important;
  border-width: 2px !important;
  box-shadow: 0 2px 8px rgba(146, 214, 227, 0.15);
}

.unapproved-contribution {
  border-color: rgba(251, 146, 60, 0.4) !important;
}

.blocked-placeholder {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px;
  background: var(--v-theme-background);
  border-radius: 12px;
  color: var(--v-theme-gray500);
}

.contribution-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.approval-status {
  color: var(--v-theme-gray500);
}

.new-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  font-weight: 600;
}

.fade-out {
  animation: fadeOut 3s forwards;
}

@keyframes fadeOut {
  0%   { opacity: 1; }
  70%  { opacity: 1; }
  100% { opacity: 0; }
}

.white-text-chip {
  color: white !important;
}

.contributor-header {
  display: flex;
  align-items: center;
}

.contributor-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--v-theme-gray600);
}

.contributor-date {
  color: var(--v-theme-gray500);
}

.contribution-message {
  background: rgba(var(--v-theme-primary), 0.05);
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.contribution-message p {
  margin: 0;
  color: var(--v-theme-gray600);
  line-height: 1.6;
  white-space: pre-wrap;
}

.empty-state {
  padding: 4rem 2rem;
}

/* ── Responsive ── */
@media (max-width: 600px) {
  .band-content {
    padding: 1.5rem 1rem;
  }

  .page-header {
    margin-bottom: 1rem;
  }

  .brand-header {
    display: none;
  }

  .mobile-logo-link {
    display: inline-flex;
    position: fixed;
    top: 0.25rem;
    left: 0.25rem;
    z-index: 1002;
  }

  .dashboard-button {
    top: 0.25rem;
    right: 0.25rem;
  }

  .hero-layout {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
  }

  .hero-info {
    text-align: center;
    max-width: 100%;
  }

  .remembering-title {
    text-align: center;
  }

  .hero-name {
    text-align: center;
    font-size: 1.75rem;
  }

  .hero-photo,
  .hero-photo-placeholder {
    width: 220px;
    height: 220px;
  }

  .photo-row {
    flex-direction: column;
    gap: 0.75rem;
  }

  .photo-left {
    width: 100%;
  }

  .photo-right {
    padding-top: 0;
  }

  .contributor-header {
    flex-wrap: wrap;
  }

  .pending-contributions-content {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
